import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

// `withCredentials` sends the HttpOnly beta-session cookie set by
// /api/onboarding/confirm-business on every subsequent request.
const client = axios.create({ baseURL: API, withCredentials: true });

// ---------- Beta session ----------
export const getSessionBusiness = () =>
  client.get(`/session/business`).then((r) => r.data.business);

export const getSessionHealth = () =>
  client.get(`/session/business/health`).then((r) => r.data);

export const logoutSession = () =>
  client.post(`/session/logout`).then((r) => r.data);

export const saveSocialPreferences = (payload) =>
  client.post(`/session/business/social-preferences`, payload).then((r) => r.data);

export const saveHelpAreas = (payload) =>
  client.post(`/session/business/help-areas`, payload).then((r) => r.data);

export const saveBriefPreference = (payload) =>
  client.post(`/session/business/brief-preference`, payload).then((r) => r.data);

// ---------- Recommendations ----------
export const seed = (reset = false) =>
  client.post(`/seed${reset ? "?reset=true" : ""}`).then((r) => r.data);

export const listRecommendations = () =>
  client.get(`/recommendations`).then((r) => r.data.recommendations);

export const getRecommendation = (id) =>
  client.get(`/recommendations/${id}`).then((r) => r.data);

export const markViewed = (id) =>
  client.post(`/recommendations/${id}/view`).then((r) => r.data);

export const actionHandle = (id, actor) =>
  client.post(`/recommendations/${id}/actions/handle`, { actor }).then((r) => r.data);

export const actionHelp = (id, payload) =>
  client.post(`/recommendations/${id}/actions/help`, payload).then((r) => r.data);

export const actionRemind = (id, payload) =>
  client.post(`/recommendations/${id}/actions/remind`, payload).then((r) => r.data);

export const actionDismiss = (id, reason = "") =>
  client.post(`/recommendations/${id}/actions/dismiss`, { reason }).then((r) => r.data);

export const completeRecommendation = (id, payload) =>
  client.post(`/recommendations/${id}/complete`, payload).then((r) => r.data);

export const addNote = (id, text) =>
  client.post(`/recommendations/${id}/notes`, { text }).then((r) => r.data);

// ---------- Dashboard ----------
export const getActionCenter = () =>
  client.get(`/dashboard/action-center`).then((r) => r.data);

// ---------- Service requests (admin) ----------
// Admin passphrase is held in a module-scoped variable only — never in
// localStorage, sessionStorage, cookies, logs, or source. It is cleared
// automatically on any full page reload (which forces the AdminGate to
// re-prompt) and can be cleared eagerly via `clearAdminToken()`.
let _adminToken = null;
export const getAdminToken = () => _adminToken;
export const setAdminToken = (t) => { _adminToken = t || null; };
export const clearAdminToken = () => { _adminToken = null; };

export const adminAuth = (passphrase) =>
  client.post(`/admin/auth`, { passphrase }).then((r) => {
    setAdminToken(r.data.token);
    return r.data.token;
  });

export const listServiceRequests = () =>
  client
    .get(`/service-requests`, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data.requests);

export const updateServiceRequest = (id, status) =>
  client
    .patch(`/service-requests/${id}?status=${status}`, null, {
      headers: { "X-Admin-Token": getAdminToken() || "" },
    })
    .then((r) => r.data);

// ---------- Briefs & content engine (admin) ----------
export const listBriefs = () =>
  client
    .get(`/briefs`, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data.briefs);

export const getBrief = (id) =>
  client
    .get(`/briefs/${id}`, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const generateBrief = (businessId) =>
  client
    .post(`/briefs/generate`, { businessId }, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const regenerateBrief = (briefId) =>
  client
    .post(`/briefs/${briefId}/regenerate`, null, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const setBriefStatus = (briefId, status) =>
  client
    .post(`/briefs/${briefId}/status`, { status }, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

// ---------- Delivery ----------
export const approveBrief = (briefId) =>
  client
    .post(`/briefs/${briefId}/approve`, { confirm: true }, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const scheduleBrief = (briefId, scheduledFor) =>
  client
    .post(`/briefs/${briefId}/schedule`, scheduledFor ? { scheduledFor } : {}, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const sendBriefNow = (briefId) =>
  client
    .post(`/briefs/${briefId}/send-now`, { confirm: true }, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const testSendBrief = (briefId, recipient) =>
  client
    .post(`/briefs/${briefId}/test-send`, { recipient }, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const cancelScheduledBrief = (briefId) =>
  client
    .post(`/briefs/${briefId}/cancel-scheduled`, null, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const retryDelivery = (deliveryId) =>
  client
    .post(`/deliveries/retry`, { deliveryId }, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

export const listBriefDeliveries = (briefId) =>
  client
    .get(`/briefs/${briefId}/deliveries`, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data.deliveries);

export const runSchedulerTick = () =>
  client
    .post(`/scheduler/tick`, null, { headers: { "X-Admin-Token": getAdminToken() || "" } })
    .then((r) => r.data);

// ---------- Public action links (no admin token) ----------
export const inspectActionToken = (token) =>
  client.get(`/actions/inspect?token=${encodeURIComponent(token)}`).then((r) => r.data);

export const consumeActionToken = (payload) =>
  client.post(`/actions/consume`, payload).then((r) => r.data);

// ---------- Customer-facing latest brief ----------
// The businessId is derived from the beta-session cookie server-side.
export const getLatestBrief = () =>
  client.get(`/brief/latest`).then((r) => r.data.brief);

// ---------- Business Discovery ----------
export const discoverBusiness = (payload) =>
  client.post(`/onboarding/discover`, payload).then((r) => r.data);

export const discoverBusinessFromUrl = (url) =>
  client.post(`/onboarding/discover/from-url`, { url }).then((r) => r.data);

export const confirmBusiness = (payload) =>
  client.post(`/onboarding/confirm-business`, payload).then((r) => r.data);

// ---------- Post-Onboarding Results + AI Visibility ----------
export const getOnboardingResults = (businessId) =>
  client.get(`/onboarding/results/${encodeURIComponent(businessId)}`).then((r) => r.data);

export const startAiVisibilitySnapshot = (businessId) =>
  client.post(`/onboarding/ai-visibility/start`, { businessId }).then((r) => r.data);

export const getAiVisibilitySnapshot = (snapshotId) =>
  client
    .get(`/onboarding/ai-visibility/snapshot/${encodeURIComponent(snapshotId)}`)
    .then((r) => r.data);

export const checkSocialPresence = (businessId) =>
  client.post(`/onboarding/social-presence/check`, { businessId }).then((r) => r.data);

export const searchSocialLinks = (businessId, channel) =>
  client.post(`/onboarding/social-links/search`, { businessId, channel }).then((r) => r.data);

export const saveSocialLink = (businessId, channel, url) =>
  client.post(`/onboarding/social-links/save`, { businessId, channel, url }).then((r) => r.data);

// ---------- Meta / Facebook Pages OAuth ----------
export const metaOauthStart = (businessId, returnTo) => {
  const qs = new URLSearchParams({ businessId });
  if (returnTo) qs.set("returnTo", returnTo);
  return client.get(`/integrations/meta/start?${qs.toString()}`).then((r) => r.data);
};

export const metaStatus = (businessId) =>
  client.get(`/integrations/meta/status?businessId=${encodeURIComponent(businessId)}`).then((r) => r.data);

export const metaDisconnect = (businessId) =>
  client.post(`/integrations/meta/disconnect`, { businessId }).then((r) => r.data);

// ---------- Admin: Businesses (Sprint 1A verification) ----------
const adminHeaders = () => ({ headers: { "X-Admin-Token": getAdminToken() || "" } });

export const listBusinesses = () =>
  client.get(`/businesses`, adminHeaders()).then((r) => r.data.businesses);

export const listAdminCustomers = () =>
  client.get(`/admin/customers`, adminHeaders()).then((r) => r.data.customers || []);

export const scheduleBriefForBusiness = (businessId) =>
  client.post(`/admin/auto-pilot/backfill`, { businessIds: [businessId] }, adminHeaders())
    .then((r) => r.data);

export const getAdminAiDiscoveryLatest = (businessId) =>
  client.get(`/admin/businesses/${businessId}/ai-discovery/latest`, adminHeaders())
    .then((r) => r.data);

export const refreshAdminAiDiscovery = (businessId) =>
  client.post(`/admin/businesses/${businessId}/ai-discovery/refresh`, null, adminHeaders())
    .then((r) => r.data);

// Sprint 3D — Website Conversion Intelligence.
export const getAdminWebsiteIntelligence = (businessId) =>
  client.get(`/admin/businesses/${businessId}/website-intelligence`, adminHeaders())
    .then((r) => r.data);

export const scanAdminWebsiteIntelligence = (businessId) =>
  client.post(`/admin/businesses/${businessId}/website-intelligence/scan`, null, adminHeaders())
    .then((r) => r.data);

export const getBusinessListingDetail = (businessId, observationStatus = "active") =>
  client.get(
    `/admin/businesses/${encodeURIComponent(businessId)}/listing-detail?observation_status=${encodeURIComponent(observationStatus)}`,
    adminHeaders(),
  ).then((r) => r.data);

export const refreshBusinessListing = (businessId) =>
  client.post(`/admin/businesses/${encodeURIComponent(businessId)}/refresh-google-listing`, {}, adminHeaders()).then((r) => r.data);

export const previewSelection = (businessId) =>
  client.get(`/admin/businesses/${encodeURIComponent(businessId)}/preview-selection`, adminHeaders()).then((r) => r.data);

export const composeBriefDraft = (businessId) =>
  client.post(`/admin/businesses/${encodeURIComponent(businessId)}/compose-brief`, {}, adminHeaders()).then((r) => r.data);

export const regenerateBriefComposition = (briefId) =>
  client.post(`/admin/briefs/${encodeURIComponent(briefId)}/regenerate-composition`, {}, adminHeaders()).then((r) => r.data);

export const getLatestBriefDraft = (businessId) =>
  client.get(`/admin/businesses/${encodeURIComponent(businessId)}/brief-draft`, adminHeaders()).then((r) => r.data);

export const getBriefComposition = (briefId) =>
  client.get(`/admin/briefs/${encodeURIComponent(briefId)}/composition`, adminHeaders()).then((r) => r.data);

export const getBriefCompositionTemplates = () =>
  client.get(`/admin/brief-composition-templates`, adminHeaders()).then((r) => r.data);

// ---- Sprint 1G Rendering & Delivery ---------------------------------------
export const createBriefRendering = (briefId) =>
  client.post(`/admin/briefs/${encodeURIComponent(briefId)}/render`, { createdBy: "admin" }, adminHeaders()).then((r) => r.data);

export const listBriefRenderings = (briefId) =>
  client.get(`/admin/briefs/${encodeURIComponent(briefId)}/renderings`, adminHeaders()).then((r) => r.data);

export const getBriefRendering = (renderingId) =>
  client.get(`/admin/brief-renderings/${encodeURIComponent(renderingId)}`, adminHeaders()).then((r) => r.data);

export const approveBriefRendering = (renderingId) =>
  client.post(`/admin/brief-renderings/${encodeURIComponent(renderingId)}/approve`, {}, adminHeaders()).then((r) => r.data);

export const sendTestBriefRendering = (renderingId, recipient) =>
  client.post(`/admin/brief-renderings/${encodeURIComponent(renderingId)}/send-test`, { recipient }, adminHeaders()).then((r) => r.data);

export const sendNowBriefRendering = (renderingId, resendPayload = null) =>
  client.post(`/admin/brief-renderings/${encodeURIComponent(renderingId)}/send-now`, resendPayload || {}, adminHeaders()).then((r) => r.data);

export const scheduleBriefRendering = (renderingId, scheduledFor = null) =>
  client.post(`/admin/brief-renderings/${encodeURIComponent(renderingId)}/schedule`, { scheduledFor }, adminHeaders()).then((r) => r.data);

export const listBriefRenderingDeliveries = (briefId) =>
  client.get(`/admin/briefs/${encodeURIComponent(briefId)}/deliveries`, adminHeaders()).then((r) => r.data);

export const getDelivery = (deliveryId) =>
  client.get(`/admin/deliveries/${encodeURIComponent(deliveryId)}`, adminHeaders()).then((r) => r.data);

export const listDeliveryEvents = (deliveryId) =>
  client.get(`/admin/deliveries/${encodeURIComponent(deliveryId)}/events`, adminHeaders()).then((r) => r.data);

export const cancelDelivery = (deliveryId) =>
  client.post(`/admin/deliveries/${encodeURIComponent(deliveryId)}/cancel`, {}, adminHeaders()).then((r) => r.data);

export const retryDeliveryV2 = (deliveryId) =>
  client.post(`/admin/deliveries/${encodeURIComponent(deliveryId)}/retry`, {}, adminHeaders()).then((r) => r.data);

export const getRendererInfo = () =>
  client.get(`/admin/renderer-info`, adminHeaders()).then((r) => r.data);

// ---- Public action confirmation (no auth) --------------------------------
export const inspectActionTokenV2 = (token) =>
  client.get(`/actions/v2/inspect`, { params: { token } }).then((r) => r.data);

export const applyActionTokenV2 = (token) =>
  client.post(`/actions/v2/apply`, { token }).then((r) => r.data);

// Preference actions from the Manage-preferences confirmation page.
// `action` is one of: "PAUSE" | "RESUME" | "UNSUBSCRIBE".
export const applyPreferenceActionV2 = (token, action) =>
  client.post(`/actions/v2/preferences`, { token, action }).then((r) => r.data);

// ---- Offer pages (Sprint 3B) ---------------------------------------------
// Read-only context loader — powers the AI Visibility and Review System
// offer pages. Returns the business name/city/state and the latest
// completed AI Discovery snapshot in simplified, customer-safe form.
export const getOfferBusinessContext = (businessId) =>
  client
    .get(`/offers/business-context/${encodeURIComponent(businessId)}`)
    .then((r) => r.data);

// Record an interest click from an offer page. Idempotent per
// (businessId, offerType) inside a short server-side window.
// `offerType`: "AI_VISIBILITY_INTEREST" | "REVIEW_SYSTEM_INTEREST".
export const createOfferInterest = ({
  businessId,
  offerType,
  sourcePage,
  additionalNotes,
}) =>
  client
    .post(`/offers/interest`, {
      businessId,
      offerType,
      sourcePage,
      additionalNotes,
    })
    .then((r) => r.data);

export default client;
